import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  SerializedError,
} from '@reduxjs/toolkit'
import {
  CreateActor,
  CreateDirector,
  CreateMovie,
  MovieItem,
  MovieListRequest,
  MoviePageTab,
  MoviePersonItem,
  PaginationMeta,
} from '../types/movie'
import {
  loadActors,
  loadDirectors,
  loadMovieList,
  loadMovies,
  modifyActor,
  modifyDirector,
  modifyMovie,
  removeActor,
  removeDirector,
  removeMovie,
  saveActor,
  saveDirector,
  saveMovie,
} from '../api/movie'

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (typeof error === 'string' && error.trim()) {
    return error
  }

  if (error && typeof error === 'object') {
    const normalizedError = error as {
      message?: string
      response?: { data?: { message?: string | string[] } }
    }

    const responseMessage = normalizedError.response?.data?.message

    if (Array.isArray(responseMessage) && responseMessage.length) {
      return responseMessage.join(' ')
    }

    if (typeof responseMessage === 'string' && responseMessage.trim()) {
      return responseMessage
    }

    if (
      typeof normalizedError.message === 'string' &&
      normalizedError.message.trim()
    ) {
      return normalizedError.message
    }
  }

  return fallbackMessage
}

export const getMovies = createAsyncThunk(
  'movie/getMovies',
  async (_, thunkApi) => {
    try {
      return await loadMovies()
    } catch (e) {
      return thunkApi.rejectWithValue('getMoviesError')
    }
  },
)

export const getMovieList = createAsyncThunk(
  'movie/getMovieList',
  async (request: MovieListRequest, thunkApi) => {
    try {
      return await loadMovieList(request)
    } catch (e) {
      return thunkApi.rejectWithValue('getMovieListError')
    }
  },
)

export const addMovie = createAsyncThunk(
  'movie/addMovie',
  async (movie: CreateMovie, thunkApi) => {
    try {
      return await saveMovie(movie)
    } catch (e) {
      return thunkApi.rejectWithValue(
        getErrorMessage(e, 'Failed to create movie.'),
      )
    }
  },
)

export const getActors = createAsyncThunk(
  'movie/getActors',
  async (_, thunkApi) => {
    try {
      return await loadActors()
    } catch (e) {
      return thunkApi.rejectWithValue('getActorsError')
    }
  },
)

export const getDirectors = createAsyncThunk(
  'movie/getDirectors',
  async (_, thunkApi) => {
    try {
      return await loadDirectors()
    } catch (e) {
      return thunkApi.rejectWithValue('getDirectorsError')
    }
  },
)

export const addActor = createAsyncThunk(
  'movie/addActor',
  async (actor: CreateActor, thunkApi) => {
    try {
      return await saveActor(actor)
    } catch (e) {
      return thunkApi.rejectWithValue(
        getErrorMessage(e, 'Failed to create actor.'),
      )
    }
  },
)

export const addDirector = createAsyncThunk(
  'movie/addDirector',
  async (director: CreateDirector, thunkApi) => {
    try {
      return await saveDirector(director)
    } catch (e) {
      return thunkApi.rejectWithValue(
        getErrorMessage(e, 'Failed to create director.'),
      )
    }
  },
)

export const updateMovie = createAsyncThunk(
  'movie/updateMovie',
  async (movie: MovieItem, thunkApi) => {
    try {
      return await modifyMovie(movie)
    } catch (e) {
      return thunkApi.rejectWithValue('updateMovieError')
    }
  },
)

export const updateActor = createAsyncThunk(
  'movie/updateActor',
  async (actor: MoviePersonItem, thunkApi) => {
    try {
      return await modifyActor(actor)
    } catch (e) {
      return thunkApi.rejectWithValue('updateActorError')
    }
  },
)

export const updateDirector = createAsyncThunk(
  'movie/updateDirector',
  async (director: MoviePersonItem, thunkApi) => {
    try {
      return await modifyDirector(director)
    } catch (e) {
      return thunkApi.rejectWithValue('updateDirectorError')
    }
  },
)

export const deleteMovie = createAsyncThunk(
  'movie/deleteMovie',
  async (id: string, thunkApi) => {
    try {
      return await removeMovie(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteMovieError')
    }
  },
)

export const deleteActor = createAsyncThunk(
  'movie/deleteActor',
  async (id: string, thunkApi) => {
    try {
      return await removeActor(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteActorError')
    }
  },
)

export const deleteDirector = createAsyncThunk(
  'movie/deleteDirector',
  async (id: string, thunkApi) => {
    try {
      return await removeDirector(id)
    } catch (e) {
      return thunkApi.rejectWithValue('deleteDirectorError')
    }
  },
)

interface MovieSliceState {
  movies: MovieItem[]
  actors: MoviePersonItem[]
  directors: MoviePersonItem[]
  pagedMovies: MovieItem[]
  pagedPeople: MoviePersonItem[]
  currentListTab?: MoviePageTab
  pagination: PaginationMeta
  activeListRequestId?: string
  loading: boolean
  error?: SerializedError
}

const emptyPagination: PaginationMeta = {
  page: 1,
  limit: 50,
  total: 0,
  totalPages: 0,
}

const initialState: MovieSliceState = {
  movies: [],
  actors: [],
  directors: [],
  pagedMovies: [],
  pagedPeople: [],
  currentListTab: undefined,
  pagination: emptyPagination,
  activeListRequestId: undefined,
  loading: false,
  error: undefined,
}

const movieSlice = createSlice({
  name: 'movie',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getMovies.fulfilled, (state, action: PayloadAction<MovieItem[]>) => {
      state.movies = action.payload
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getMovies.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getMovies.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(getMovieList.fulfilled, (state, action: any) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      if (
        state.currentListTab === 'movies' ||
        state.currentListTab === 'series' ||
        state.currentListTab === 'animations'
      ) {
        state.pagedMovies = action.payload.items as MovieItem[]
        state.pagedPeople = []
      } else {
        state.pagedPeople = action.payload.items as MoviePersonItem[]
        state.pagedMovies = []
      }

      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages,
      }
      state.activeListRequestId = undefined
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getMovieList.pending, (state, action: any) => {
      state.currentListTab = action.meta.arg.tab
      state.activeListRequestId = action.meta.requestId
      state.loading = true
      state.error = undefined

      if (
        action.meta.arg.tab === 'movies' ||
        action.meta.arg.tab === 'series' ||
        action.meta.arg.tab === 'animations'
      ) {
        state.pagedMovies = []
      } else {
        state.pagedPeople = []
      }
    })
    builder.addCase(getMovieList.rejected, (state, action: any) => {
      if (state.activeListRequestId !== action.meta.requestId) {
        return
      }

      state.activeListRequestId = undefined
      state.loading = false
      state.error = action.error
    })
    builder.addCase(addMovie.fulfilled, (state, action: PayloadAction<MovieItem>) => {
      state.movies = [action.payload, ...state.movies]
      state.loading = false
      state.error = undefined
    })
    builder.addCase(addMovie.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addMovie.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(getActors.fulfilled, (
      state,
      action: PayloadAction<MoviePersonItem[]>,
    ) => {
      state.actors = action.payload
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getActors.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getActors.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(getDirectors.fulfilled, (
      state,
      action: PayloadAction<MoviePersonItem[]>,
    ) => {
      state.directors = action.payload
      state.loading = false
      state.error = undefined
    })
    builder.addCase(getDirectors.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(getDirectors.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(addActor.fulfilled, (
      state,
      action: PayloadAction<MoviePersonItem>,
    ) => {
      state.actors = [action.payload, ...state.actors]
      state.loading = false
      state.error = undefined
    })
    builder.addCase(addActor.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addActor.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(addDirector.fulfilled, (
      state,
      action: PayloadAction<MoviePersonItem>,
    ) => {
      state.directors = [action.payload, ...state.directors]
      state.loading = false
      state.error = undefined
    })
    builder.addCase(addDirector.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(addDirector.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(updateMovie.fulfilled, (state, action: PayloadAction<MovieItem>) => {
      state.movies = state.movies.map((movie) =>
        movie.id === action.payload.id ? action.payload : movie,
      )
      state.pagedMovies = state.pagedMovies.map((movie) =>
        movie.id === action.payload.id ? action.payload : movie,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(updateMovie.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateMovie.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(updateActor.fulfilled, (
      state,
      action: PayloadAction<MoviePersonItem>,
    ) => {
      state.actors = state.actors.map((actor) =>
        actor.id === action.payload.id ? action.payload : actor,
      )
      state.pagedPeople = state.pagedPeople.map((actor) =>
        actor.id === action.payload.id ? action.payload : actor,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(updateActor.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateActor.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(updateDirector.fulfilled, (
      state,
      action: PayloadAction<MoviePersonItem>,
    ) => {
      state.directors = state.directors.map((director) =>
        director.id === action.payload.id ? action.payload : director,
      )
      state.pagedPeople = state.pagedPeople.map((director) =>
        director.id === action.payload.id ? action.payload : director,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(updateDirector.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(updateDirector.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(deleteMovie.fulfilled, (state, action: PayloadAction<string>) => {
      state.movies = state.movies.filter((movie) => movie.id !== action.payload)
      state.pagedMovies = state.pagedMovies.filter(
        (movie) => movie.id !== action.payload,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(deleteMovie.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteMovie.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(deleteActor.fulfilled, (state, action: PayloadAction<string>) => {
      state.actors = state.actors.filter((actor) => actor.id !== action.payload)
      state.pagedPeople = state.pagedPeople.filter(
        (actor) => actor.id !== action.payload,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(deleteActor.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteActor.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
    builder.addCase(deleteDirector.fulfilled, (state, action: PayloadAction<string>) => {
      state.directors = state.directors.filter(
        (director) => director.id !== action.payload,
      )
      state.pagedPeople = state.pagedPeople.filter(
        (director) => director.id !== action.payload,
      )
      state.loading = false
      state.error = undefined
    })
    builder.addCase(deleteDirector.pending, (state) => {
      state.loading = true
      state.error = undefined
    })
    builder.addCase(deleteDirector.rejected, (state, action) => {
      state.loading = false
      state.error = action.error
    })
  },
})

export default movieSlice.reducer
